/**
 * StaticChildManager - Manages rendering for spanned and span-stacked rows.
 *
 * Two rendering strategies:
 * - **Spanned**: Row timescale > sheet timescale. Single static child fills
 *   the viewport (e.g., monthly row on day sheet).
 * - **Span-stacked**: Sheet timescale < minWidthTimescale. Fixed children
 *   positioned at date ranges, each containing a CellGroup with subcells
 *   stacked at the sheet timescale (e.g., text row on day sheet → week-wide
 *   spans with 7 day subcells).
 *
 * Lifecycle: register/registerSpanStacked → computeVisiblePlans → applyPlans → clear
 */

import { nonnull } from "@tento-core/assert";
import { DateTime, PartialDate, Utc } from "@tento-chrono";
import { ColumnIndex } from "@tento-ui/components/sheet/column-index";
import { SheetViewModel } from "@tento-lona";
import { SheetCell } from "@tento-ui/components/sheet/sheet-cell";
import { CellGroup } from "@tento-ui/components/sheet/cell-group";
import { SheetDelegate } from "./sheet-delegate.ts";
import { rowIdentity } from "@tento-ui/components/sheet/row-identity";
import type { Option, RowLocalId } from "@tento-lona/sheets";
import { type SheetRow } from "@tento-lona";
import { DebugAttributes } from "@tento-ui/components/sheet/debug-attributes";
import { TypedColumn } from "./column-typed.ts";
import {
  ColumnTimeState,
  DefaultBinderHelpers,
} from "./binder/default-binder-helpers.ts";
import { Timescale } from "@tento-lona/sheets";
import type { SheetBinder } from "./sheet-index-renderer.ts";

/**
 * Information about a registered static child span.
 */
export interface StaticChildRegistration {
  row: SheetRow.Joined.Handle;
  spanCount: number;
  /** Currently visible period - used to detect when content needs refresh */
  currentPeriod: Option<PartialDate>;
  /** For span-stacked: the timescale of the span (e.g., "week") */
  spanTimescale: Option<Timescale>;
  /** For span-stacked: the sheet timescale for stacking subcells */
  sheetTimescale: Option<Timescale>;
}

/**
 * Plan for rendering a static child.
 */
export interface StaticChildRenderPlan {
  rowId: RowLocalId;
  visiblePeriod: PartialDate;
  layout: SheetCell.Layout;
  timeState: ColumnTimeState;
  spanWidthPx: number;
  isNew: boolean;
  /** Whether the visible period changed (requires content refresh) */
  periodChanged: boolean;
}

/**
 * Manager for static child rendering.
 * Encapsulates all logic for tracking and rendering spans that exceed the viewport.
 */
export class StaticChildManager {
  /**
   * Registered spans that need static child rendering.
   * Key: rowId (only one span per row)
   */
  #registrations = new Map<RowLocalId, StaticChildRegistration>();

  #onBlurCell: Option<(cell: SheetCell.CellId) => void>;
  #onFocusCell: Option<(cell: SheetCell.CellId) => void>;
  #handle: SheetDelegate;
  constructor(
    handle: SheetDelegate,
    onFocusCell: Option<(cell: SheetCell.CellId) => void>,
    onBlurCell: Option<(cell: SheetCell.CellId) => void>,
  ) {
    this.#handle = handle;
    this.#onFocusCell = onFocusCell;
    this.#onBlurCell = onBlurCell;
  }

  /**
   * Phase 1: Register a row for static child rendering.
   * Called during onBind when a row is determined to need static rendering.
   */
  register(
    rowId: RowLocalId,
    row: SheetRow.Joined.Handle,
    sheetTimescale: Timescale,
  ): void {
    if (this.#registrations.has(rowId)) return;
    if (row.timescaleSetting === "custom") return;

    const spanCount = Timescale.conservativeSpanCount(row.timescaleSetting, sheetTimescale);

    DebugAttributes.trace("static-child-manager/register", {
      rowId,
      timescaleSetting: row.timescaleSetting,
      sheetTimescale,
      spanCount,
    });

    this.#registrations.set(rowId, {
      row,
      spanCount,
      currentPeriod: null,
      spanTimescale: null,
      sheetTimescale: null,
    });
  }

  /**
   * Register a row for span+stack rendering.
   * The row will be rendered as fixed children spanning `spanTimescale` columns,
   * with subcells stacked at `sheetTimescale` inside each span.
   */
  registerSpanStacked(
    rowId: RowLocalId,
    row: SheetRow.Joined.Handle,
    sheetTimescale: Timescale,
    spanTimescale: Timescale,
    spanCount: number,
  ): void {
    if (this.#registrations.has(rowId)) return;

    DebugAttributes.trace("static-child-manager/registerSpanStacked", {
      rowId,
      sheetTimescale,
      spanTimescale,
      spanCount,
    });

    this.#registrations.set(rowId, {
      row,
      spanCount,
      currentPeriod: null,
      spanTimescale,
      sheetTimescale: sheetTimescale,
    });
  }

  /**
   * Unregister a row from static child rendering.
   * Called when a row transitions out of static mode.
   */
  unregister(rowId: RowLocalId): boolean {
    const registration = this.#registrations.get(rowId);
    if (!registration) return false;

    DebugAttributes.trace("static-child-manager/unregister", { rowId });

    if (registration.spanTimescale) {
      // Span-stacked rows use fixedChildren — clear by owner
      this.#handle.fixedChild.clearFixedChildrenByOwner("span-stacked");
    } else {
      this.#handle.staticChild.dismiss("span", rowId);
    }
    this.#registrations.delete(rowId);
    return true;
  }

  /**
   * Check if a row is registered for static child rendering.
   */
  has(rowId: RowLocalId): boolean {
    return this.#registrations.has(rowId);
  }

  /**
   * Get the count of registered static children.
   */
  get size(): number {
    return this.#registrations.size;
  }

  /**
   * Phase 2: Compute render plans for all registered static children.
   * Pure computation - no DOM mutation.
   */
  computeVisiblePlans(
    sheet: SheetViewModel.Surface<SheetBinder>,
    visibleColumns: ColumnIndex.Key.Resolved.Default.Range,
    now: DateTime<Utc>,
  ): StaticChildRenderPlan[] {
    if (this.#registrations.size === 0) return [];

    const defaultWidthPx = this.#handle.state.resolvedWidthPx;
    const plans: StaticChildRenderPlan[] = [];

    for (const [rowId, registration] of this.#registrations.entries()) {
      const { row, spanCount, spanTimescale } = registration;

      // For span-stacked rows, compute the containing period at the span timescale
      // (e.g., the containing week for a day-ts text row with minWidthTimescale=week).
      // For regular spanned rows, use the row's own timescale.
      const periodTimescale = spanTimescale ?? (row.timescaleSetting as Timescale);
      const visiblePeriod = Timescale.containingPeriod(
        visibleColumns.start.pd,
        periodTimescale,
      );

      // Get layout for this row
      const layout = sheet.layout.rowLayout.get(rowId);
      if (!layout) continue;

      // Calculate span width
      const spanWidthPx = spanCount * defaultWidthPx;

      // Check if static child already exists
      const $existing = this.#handle.staticChild.get("span", rowId);
      const isNew = !$existing;

      // Check if period changed (requires content refresh)
      const previousPeriod = registration.currentPeriod;
      const periodChanged =
        !previousPeriod || !previousPeriod.equals(visiblePeriod);

      // Compute time state based on visible period's end
      const periodEndKey = ColumnIndex.Key.fromPartial(
        PartialDate.fromDate(visiblePeriod.end.addDays(-1)),
        "upper",
      );
      const periodEndColumn = sheet.indexRenderer.mapper.resolveKeyToDefault(
        periodEndKey,
        "upper",
      );
      const timeState = DefaultBinderHelpers.computeColumnTimeState(now, periodEndColumn.range);

      plans.push({
        rowId,
        visiblePeriod,
        layout,
        timeState,
        spanWidthPx,
        isNew,
        periodChanged,
      });
    }

    DebugAttributes.trace("static-child-manager/computeVisiblePlans", {
      registrationCount: this.#registrations.size,
      planCount: plans.length,
    });

    return plans;
  }

  /**
   * Phase 3: Apply render plans to create/update DOM elements.
   */
  applyPlans(
    plans: StaticChildRenderPlan[],
    sheet: SheetViewModel.Surface<SheetBinder>,
    now: DateTime<Utc>,
    visibleColumns: ColumnIndex.Key.Resolved.Default.Range,
  ): void {
    for (const plan of plans) {
      const registration = this.#registrations.get(plan.rowId);
      if (!registration) continue;

      if (registration.spanTimescale) {
        this.#applySpanStackedPlan(
          plan,
          registration,
          sheet,
          now,
          visibleColumns,
        );
      } else {
        this.#applySpannedPlan(plan, registration, sheet, now);
      }
    }

    DebugAttributes.trace("static-child-manager/applyPlans", {
      planCount: plans.length,
    });
  }

  /**
   * Apply a plan for a regular spanned row (single cell, fillWidth).
   */
  #applySpannedPlan(
    plan: StaticChildRenderPlan,
    registration: StaticChildRegistration,
    sheet: SheetViewModel.Surface<SheetBinder>,
    now: DateTime<Utc>,
  ): void {
    const { row } = registration;

    let $row = this.#handle.staticChild.get(
      "span",
      plan.rowId,
    ) as Option<CellGroup.Row>;

    if (!$row) {
      $row = CellGroup.Row.make();
      $row.onFocusCell = this.#onFocusCell;
      $row.onBlurCell = this.#onBlurCell;
    }

    const { visiblePeriod, timeState, spanWidthPx, layout } = plan;

    const periodEndKey = ColumnIndex.Key.fromPartial(
      PartialDate.fromDate(visiblePeriod.end.addDays(-1)),
      "upper",
    );
    const periodEndColumn = sheet.indexRenderer.mapper.resolveKeyToDefault(
      periodEndKey,
      "upper",
    );

    const rowTimescaleColumn = TypedColumn.makeRowTimescale(
      row.timescaleSetting as Timescale,
      visiblePeriod,
      periodEndColumn.tz,
      periodEndColumn.offset,
    );

    const needsContentRefresh = plan.isNew || plan.periodChanged;

    if (needsContentRefresh) {
      const binding = DefaultBinderHelpers.resolveCellBinding(row, this.#handle.pluginManager);
      $row.bind({
        pd: visiblePeriod,
        cellHeightPx: layout.cellHeightPx,
        inThePast: timeState.inThePast,
        isCurrent: timeState.isCurrent,
        rowType: row.descriptor.id,
        $content: binding.plugin.makeCell(),
        cellId: {
          row: rowIdentity(plan.rowId.toString()),
          col: rowTimescaleColumn,
        },
        layout,
      });
      DefaultBinderHelpers.bindCellPluginAttributes($row.cell, binding.binding);

      DefaultBinderHelpers.bindPluginCells($row.cell, rowTimescaleColumn, null, {
        sheet,
        row,
        widthPx: spanWidthPx,
        inThePast: timeState.inThePast,
        isCurrent: timeState.isCurrent,
        handle: this.#handle,
        now,
      });

      registration.currentPeriod = visiblePeriod;
    }

    if (plan.isNew) {
      this.#handle.staticChild.reveal("span", $row, plan.rowId, {
        fillWidth: true,
      });
    }
  }

  /**
   * Apply a plan for a span-stacked row using fixedChildren.
   * Each visible span period becomes a fixed child positioned at its
   * date range, with subcells stacked vertically inside.
   */
  #applySpanStackedPlan(
    plan: StaticChildRenderPlan,
    registration: StaticChildRegistration,
    sheet: SheetViewModel.Surface<SheetBinder>,
    now: DateTime<Utc>,
    visibleColumns: ColumnIndex.Key.Resolved.Default.Range,
  ): void {
    const { row, spanCount, sheetTimescale, spanTimescale } = registration;
    if (!sheetTimescale || !spanTimescale) return;

    const { layout } = plan;
    const subcellHeightPx = Math.floor(layout.cellHeightPx / spanCount);

    // Compute all span periods that intersect the visible columns.
    // Walk from the containing period of the first visible column to the last.
    const startPeriod = Timescale.containingPeriod(
      visibleColumns.start.pd,
      spanTimescale,
    );
    const endPeriod = Timescale.containingPeriod(visibleColumns.end.pd, spanTimescale);
    const spanPeriods: PartialDate[] = [];
    let cursor = startPeriod;
    for (let safety = 0; safety < 20; safety++) {
      spanPeriods.push(cursor);
      if (cursor.equals(endPeriod)) break;
      cursor = cursor.add(1);
    }

    const self = this;
    const specs: SheetDelegate.FixedChildSpec[] = spanPeriods.map((spanPd) => ({
      id: `span-stacked:${plan.rowId}:${spanPd.start.toString()}`,
      x: {
        type: "date-range" as const,
        startDate: spanPd.start,
        endDate: spanPd.end.addDays(-1),
      },
      inset: { top: 3, left: -2 },
      widthBehavior: "fill" as const,
      makeElement() {
        return self.#buildSpanStackedElement(
          spanPd,
          row,
          sheetTimescale,
          spanCount,
          subcellHeightPx,
          layout,
          sheet,
          now,
        );
      },
      onUpdate($el) {
        if ($el instanceof CellGroup) {
          $el.bindConfig({
            individualCellHeight: subcellHeightPx,
            visibleCellCount: spanCount,
            seeMoreHeight: 0,
            totalCells: spanCount,
          });
          $el.style.height = layout.cellHeightPx + "px";

          // Re-bind plugin cells with current row data. The CellGroup
          // is created once (makeElement → buildSpanStackedElement) and
          // only receives onUpdate calls afterward. Without this rebind,
          // subcells keep the data from initial creation — which is empty
          // when async cell data (e.g., TasksIndex) hasn't arrived yet.
          const subPartials = spanPd.generateSubPartials(sheetTimescale, spanCount);
          const startKey = ColumnIndex.Key.fromPartial(
            PartialDate.fromDate(spanPd.start), "lower",
          );
          const startCol = sheet.indexRenderer.mapper.resolveKeyToDefault(startKey, "lower");
          const subcells = $el.getSubcells();
          for (let i = 0; i < subcells.length && i < subPartials.length; i++) {
            const subColumn = new ColumnIndex.Key.Resolved.Default(
              nonnull(subPartials[i], "a sub-partial per subcell"), startCol.tz, startCol.offset + i, null, null,
            );
            const { inThePast, isCurrent } = DefaultBinderHelpers.computeColumnTimeState(now, subColumn.range);
            DefaultBinderHelpers.bindPluginCells(subcells[i] as SheetCell, subColumn, null, {
              sheet, row, widthPx: 0, inThePast, isCurrent,
              handle: self.#handle, now,
            });
          }
        }
      },
    }));

    this.#handle.fixedChild.setFixedChildrenForRow(plan.rowId, specs, {
      owner: "span-stacked",
    });
  }

  /**
   * Build the DOM element for a single span-stacked period.
   * Uses the canonical CellGroup component — identical to the stacked
   * rendering path in CellContentBuilder.buildStackedContent().
   */
  #buildSpanStackedElement(
    spanPd: PartialDate,
    row: SheetRow.Joined.Handle,
    sheetTimescale: Timescale,
    spanCount: number,
    subcellHeightPx: number,
    layout: SheetCell.Layout,
    sheet: SheetViewModel.Surface<SheetBinder>,
    now: DateTime<Utc>,
  ): HTMLElement {
    const subPartials = spanPd.generateSubPartials(sheetTimescale, spanCount);

    // Resolve tz from span start
    const startKey = ColumnIndex.Key.fromPartial(
      PartialDate.fromDate(spanPd.start),
      "lower",
    );
    const startColumn = sheet.indexRenderer.mapper.resolveKeyToDefault(
      startKey,
      "lower",
    );
    const tz = startColumn.tz;
    const baseOffset = startColumn.offset;

    // Build subcell SheetCells — matches createSubcell() in default-binder-helpers
    const { background: _, ...rowOptionsWithoutBackground } =
      layout.options ?? {};
    const $sheetCells: SheetCell[] = [];

    for (const [i, subPd] of subPartials.entries()) {
      const subColumn = new ColumnIndex.Key.Resolved.Default(
        subPd,
        tz,
        baseOffset + i,
        null,
        null,
      );
      const { inThePast, isCurrent } = DefaultBinderHelpers.computeColumnTimeState(
        now,
        subColumn.range,
      );

      const binding = DefaultBinderHelpers.resolveCellBinding(row, this.#handle.pluginManager);
      const $pluginCell = binding.plugin.makeCell();
      const $subcell = SheetCell.make() as SheetCell;
      DefaultBinderHelpers.bindCellPluginAttributes($subcell, binding.binding);
      $subcell.setId({ row: rowIdentity(row.id.toString()), col: subColumn });
      $subcell.onFocusCell = this.#onFocusCell;
      $subcell.onBlurCell = this.#onBlurCell;
      $subcell.bindLayoutOptions(
        {
          card: true,
          canDrawCardBorder: false,
          marginInline: "0px",
          marginBlock: "0px",
          ...rowOptionsWithoutBackground,
        },
        inThePast,
        isCurrent,
      );
      $subcell.bindContent(binding.contentType, $pluginCell);
      $sheetCells.push($subcell);

      // Bind plugin data
      DefaultBinderHelpers.bindPluginCells($subcell, subColumn, null, {
        sheet,
        row,
        widthPx: 0,
        inThePast,
        isCurrent,
        handle: this.#handle,
        now,
      });
    }

    // Build CellGroup — the canonical stacked container
    const $cellGroup = CellGroup.make();
    $cellGroup.onFocusCell = this.#onFocusCell;
    $cellGroup.onBlurCell = this.#onBlurCell;
    $cellGroup.bind($sheetCells, {
      individualCellHeight: subcellHeightPx,
      visibleCellCount: subPartials.length,
      seeMoreHeight: 0,
      totalCells: spanCount,
    });
    // Override height: 100% — fixed children are absolutely positioned so
    // 100% resolves to the scroll container, not the row.
    $cellGroup.style.height = layout.cellHeightPx + "px";

    return $cellGroup;
  }

  /**
   * Update all static children for the current visible columns.
   * Combines computeVisiblePlans and applyPlans in one call.
   */
  update(
    sheet: SheetViewModel.Surface<SheetBinder>,
    visibleColumns: ColumnIndex.Key.Resolved.Default.Range,
    now: DateTime<Utc>,
  ): void {
    // Prune registrations for rows that are no longer in the sheet. The manager
    // only ever ADDS registrations (from `onBind` when a row enters a spanning
    // mode) and unregisters them via `cleanupRowMode` when a still-*bound* row's
    // mode changes — a row that is REMOVED outright never re-binds, so without
    // this its span / span-stacked overlay (a `staticChild` span or a
    // `fixedChild` owner="span-stacked") would linger as a blank, height-bearing
    // "phantom-gap" zombie. Every structural-reconcile path funnels through
    // `update` (rebindStructure, rebindAll, viewport, drag, first paint), so
    // pruning here by stable row identity reconciles overlays on any change.
    if (this.#registrations.size > 0) {
      const live = new Set(
        sheet.joinedTree.flattened.rows.map((r) => r.id.toString()),
      );
      for (const rowId of [...this.#registrations.keys()]) {
        if (!live.has(rowId.toString())) this.unregister(rowId);
      }
    }
    const plans = this.computeVisiblePlans(sheet, visibleColumns, now);
    this.applyPlans(plans, sheet, now, visibleColumns);
  }

  /**
   * Phase 4: Clear all static children and registrations.
   */
  clear(): void {
    DebugAttributes.trace("static-child-manager/clear", {
      count: this.#registrations.size,
    });

    let hasSpanStacked = false;
    for (const [rowId, registration] of this.#registrations.entries()) {
      if (registration.spanTimescale) {
        hasSpanStacked = true;
      } else {
        this.#handle.staticChild.dismiss("span", rowId);
      }
    }
    if (hasSpanStacked) {
      this.#handle.fixedChild.clearFixedChildrenByOwner("span-stacked");
    }
    this.#registrations.clear();
  }

  /**
   * Get all registered row IDs (for debugging/testing).
   */
  getRegisteredRowIds(): RowLocalId[] {
    return Array.from(this.#registrations.keys());
  }
}
